import React, { useState } from "react";
import { CheckCircle2, Lock, Radio, ChevronDown, Play, Circle, BookOpen } from "lucide-react";
import { formatDay } from "../../Syllabus/schedule/scheduleFormat";

/**
 * SessionNavTree — compact left-panel navigation list.
 * Shows modules (collapsible) with session items inside each.
 * Visual state per session: completed ✓ / live ● / locked 🔒 / selected ▶ / default ○
 */
const SessionNavTree = ({ modules, selectedSessionId, onSelectSession }) => {
  const [openModules, setOpenModules] = useState(() =>
    (modules || []).reduce((acc, _, i) => ({ ...acc, [i]: i === 0 }), {}),
  );

  const toggle = (i) => setOpenModules((prev) => ({ ...prev, [i]: !prev[i] }));

  return (
    <nav className="lp-nav">
      <div className="lp-nav__head">Your Learning Path</div>
      {(modules || []).map((mod, mi) => {
        const sessions = mod.sessions || [];
        const total = sessions.length;
        const done = sessions.filter(
          (s) => s.student?.state === "completed" || s.student?.state === "mastered",
        ).length;
        const pct = total ? Math.round((done / total) * 100) : 0;
        const isOpen = openModules[mi] ?? mi === 0;

        return (
          <div key={mod.moduleNumber} className="lp-nav__module">
            <button
              type="button"
              className={`lp-nav__mod-head ${isOpen ? "lp-nav__mod-head--open" : ""}`}
              onClick={() => toggle(mi)}
            >
              <div className="lp-nav__mod-info">
                <span className="lp-nav__mod-name">
                  Module {mod.moduleNumber}: {mod.title}
                </span>
                <span className="lp-nav__mod-stats">
                  {total > 0
                    ? `${done}/${total} sessions completed`
                    : `${(mod.topics || []).length} topic${(mod.topics || []).length === 1 ? "" : "s"} · coming up`}
                </span>
              </div>
              <div className="lp-nav__mod-right">
                {total > 0 && <span className="lp-nav__mod-pct">{pct}%</span>}
                <ChevronDown
                  size={14}
                  className={`lp-nav__chev ${isOpen ? "lp-nav__chev--open" : ""}`}
                />
              </div>
            </button>

            {isOpen && (
              <div className="lp-nav__sessions">
                {sessions.map((s) => {
                  const state = s.student?.state || "available";
                  const isSelected = s._id === selectedSessionId;
                  const isCompleted = state === "completed" || state === "mastered";
                  const isLocked = state === "locked";
                  const isLive = state === "live";

                  const completedDate =
                    s.endedAt && isCompleted
                      ? new Date(s.endedAt).toLocaleDateString("en-US", {
                          day: "numeric",
                          month: "short",
                        })
                      : null;

                  return (
                    <button
                      key={s._id}
                      type="button"
                      className={[
                        "lp-nav__session",
                        isSelected && "lp-nav__session--active",
                        isLocked && "lp-nav__session--locked",
                        isLive && "lp-nav__session--live",
                        isCompleted && "lp-nav__session--done",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() =>
                        !isLocked &&
                        onSelectSession({
                          ...s,
                          moduleNumber: mod.moduleNumber,
                          moduleTitle: mod.title,
                        })
                      }
                      disabled={isLocked}
                    >
                      <span className="lp-nav__session-icon">
                        {isCompleted ? (
                          <CheckCircle2 size={14} />
                        ) : isLive ? (
                          <Radio size={12} />
                        ) : isLocked ? (
                          <Lock size={11} />
                        ) : isSelected ? (
                          <Play size={11} />
                        ) : (
                          <Circle size={11} />
                        )}
                      </span>
                      <div className="lp-nav__session-text">
                        <span className="lp-nav__session-label">
                          {mod.moduleNumber}.{s.order} {s.title}
                        </span>
                        {isCompleted && completedDate ? (
                          <span className="lp-nav__session-hint">
                            Completed {completedDate}
                          </span>
                        ) : isLive ? (
                          <span className="lp-nav__session-hint lp-nav__session-hint--live">
                            Live session in progress
                          </span>
                        ) : isLocked ? (
                          <span className="lp-nav__session-hint">
                            Unlocks after {mod.moduleNumber}.{s.order - 1}
                            {s.plannedDate ? ` · class on ${formatDay(s.plannedDate)}` : ""}
                          </span>
                        ) : s.plannedDate ? (
                          <span className="lp-nav__session-hint">
                            Class on {formatDay(s.plannedDate)}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}

                {/* Syllabus outline — every topic/subtopic so students see the
                    full course plan even where sessions/resources aren't up yet. */}
                {(mod.topics || []).length > 0 && (
                  <div className="lp-nav__syllabus">
                    <span className="lp-nav__syllabus-head">
                      <BookOpen size={11} /> Syllabus
                    </span>
                    {mod.topics.map((t, ti) => (
                      <div key={ti} className="lp-nav__topic">
                        <span className="lp-nav__topic-title">{t.title}</span>
                        {Array.isArray(t.subtopics) && t.subtopics.length > 0 && (
                          <ul className="lp-nav__subtopics">
                            {t.subtopics.map((st, si) => (
                              <li key={si} className="lp-nav__subtopic">{st}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default SessionNavTree;
